// Axios Instance with Interceptors

import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS } from '../config/api.config';
import StorageUtil from '../utils/storage';
import { forceLogoutRedirect, isSessionRedirecting } from '../utils/sessionGuard';
import { canHijackPath } from '../utils/authRealms';

const SESSION_EXEMPT_ENDPOINTS = [
  API_ENDPOINTS.AUTH.LOGIN,
  API_ENDPOINTS.AUTH.LOGOUT,
  API_ENDPOINTS.AUTH.REFRESH_TOKEN,
  API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
  API_ENDPOINTS.AUTH.RESET_PASSWORD,
];

const isSessionExemptUrl = (url) =>
  SESSION_EXEMPT_ENDPOINTS.some((endpoint) => url?.includes(endpoint));

const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor - Add Token to Headers
axiosInstance.interceptors.request.use(
  (config) => {
    // Public/session-exempt calls (login, logout, refresh, forgot/reset password)
    // must never carry a stale or invalid token — if one happens to be sitting in
    // localStorage from a previous session, attaching it here can make the backend
    // reject the request outright (e.g. "Invalid token.") before it even looks at
    // the submitted credentials.
    if (isSessionExemptUrl(config.url)) {
      return config;
    }

    const token = StorageUtil.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - Handle Errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url;
    const isSessionExempt = isSessionExemptUrl(requestUrl);

    // An authenticated request with an invalid/expired token ends the session.
    // Auth endpoints are excluded so invalid login credentials stay on the form.
    if (error.response?.status === 401 && !isSessionExempt) {
      // Redirect only a tab that is actually IN the superadmin workspace.
      // In any other tab (ops, cars, login) the dead session is just cleared —
      // three logins share this browser and each keeps to its own screen.
      if (canHijackPath('superadmin', window.location.pathname)) {
        forceLogoutRedirect('session_expired');
      } else {
        StorageUtil.clearAuth();
      }

      // A hard navigation is already underway (here, or from the idle-timeout /
      // token-expiry triggers in ProtectedRoute). Never resolve this promise so
      // the calling thunk/component stays in its pending state instead of
      // rendering the raw backend error (e.g. "Invalid token.") for the instant
      // before the browser actually leaves the page.
      if (isSessionRedirecting()) {
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
